import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import { AgencyStates } from '@rsos/capstone/src/constants/Systems';
import { Box, Button, Select, Text } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import {
  getInitialPhoneValue,
  sanitizedPhoneNumber,
} from '@rsos/utils/phoneNumberUtils';
import { notifySuccess, notifyError } from '@rsos/utils/toastUtils';
import { updatePSAPInfo } from '../../actions/psap';
import {
  Title,
  Description,
  SelectWrapper,
  ThreeItemRow,
  TwoItemRow,
} from './AgencyInfoForm.styles';
import {
  rspFormValidationSchema,
  cspFormValidationSchema,
  selectBuildOptions,
  getInitialStateValue,
} from './utils';

const AgencyInfoForm = () => {
  const dispatch = useDispatch();
  const { application } = useSelector(state => state.sinatra.user.currentRole);
  const { currentPSAP } = useSelector(state => state.psaps);
  const isCentralStation = application === 'central_station';

  const {
    fcc_id,
    display_name,
    agency_state,
    contact_name,
    contact_title,
    contact_email,
    contact_phone,
    non_emergency_phone,
    population,
  } = currentPSAP;

  const [agencyState, setAgencyState] = useState(
    getInitialStateValue(AgencyStates, agency_state) || 'Select',
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = values => {
    setIsLoading(true);
    const {
      agency_state,
      contact_name,
      display_name,
      contact_title,
      contact_phone,
      non_emergency_phone,
      fcc_id,
      population,
      contact_email,
    } = values;
    const psapInfo = {
      state: agency_state.value,
      contact_name,
      contact_email,
      contact_phone: sanitizedPhoneNumber(contact_phone),
      contact_title,
      display_name,
      fcc_id,
      non_emergency_phone: sanitizedPhoneNumber(non_emergency_phone),
      population: parseInt(population, 10),
      phone_system: currentPSAP['phone_system'],
      cad_system: currentPSAP['cad_system'],
      mapping_system: currentPSAP['mapping_system'],
    };

    dispatch(updatePSAPInfo(psapInfo))
      .then(() => {
        notifySuccess('ECC Info Successfully updated.');
      })
      .catch(() => {
        notifyError(
          "Unable to update your ECC's information. Please try again. If this error persists, submit a ticket via the 'Support' tab.",
        );
      })
      .finally(() => setIsLoading(false));
  };

  const handleStateChange = (selectedOption, values, cb) => {
    values['agency_state'] = selectedOption.value;
    setAgencyState(selectedOption);
    cb('agency_state', selectedOption);
  };
  const getFormSchemaValidation = () => {
    return application === 'central_station'
      ? cspFormValidationSchema
      : rspFormValidationSchema;
  };

  return (
    <>
      <Title>{isCentralStation ? 'Account Info' : 'Agency Info'}</Title>
      <Formik
        initialValues={{
          fcc_id: fcc_id || '',
          display_name: display_name || '',
          agency_state: agencyState,
          contact_name: contact_name || '',
          contact_title: contact_title || '',
          contact_phone: getInitialPhoneValue(contact_phone),
          contact_email: contact_email || '',
          non_emergency_phone: getInitialPhoneValue(non_emergency_phone),
          population: population || '',
        }}
        validationSchema={getFormSchemaValidation()}
        validateOnMount={true}
        onSubmit={values => {
          handleSubmit(values);
        }}
      >
        {({
          values,
          handleChange,
          isValid,
          handleBlur,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
        }) => (
          <>
            <Box horizontal justify="space-between" align="center" mb={20}>
              <Description>
                {isCentralStation
                  ? 'Be sure to maintain the correct details about your Central Station.'
                  : 'Maintain your agency detail'}
              </Description>
              <Button
                dataName="admin-save-psap-info-button"
                type="submit"
                form="agency-info-form"
                disabled={!isValid}
                isLoadingProp={isLoading}
              >
                Save Changes
              </Button>
            </Box>
            <Form id="agency-info-form">
              <Box flow={24}>
                <ThreeItemRow>
                  {isCentralStation ? null : (
                    <InputText
                      id="fcc_id"
                      placeholder="0000"
                      label="FCC PSAP ID (US Only)"
                      type="name"
                      value={values.fcc_id}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      error={
                        errors.fcc_id && (!!values.fcc_id || touched.fcc_id)
                          ? errors.fcc_id
                          : null
                      }
                      shouldErrorTakeSpace
                    />
                  )}
                  <Box>
                    <InputText
                      id="display_name"
                      placeholder={
                        isCentralStation ? 'Organization Name' : 'Agency Name'
                      }
                      type="text"
                      label={
                        isCentralStation
                          ? 'Organization Name *'
                          : 'Agency Name *'
                      }
                      value={values.display_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      shouldErrorTakeSpace
                      error={
                        errors.display_name &&
                        (!!values.display_name || touched.display_name)
                          ? errors.display_name
                          : null
                      }
                    />
                  </Box>
                  {isCentralStation ? null : (
                    <SelectWrapper>
                      <Text size="normal" color="primaryText">
                        State or Province *
                      </Text>
                      <Select
                        options={AgencyStates.map(selectBuildOptions)}
                        placeholder="Select your State"
                        isClearable={false}
                        onChange={selectedOption => {
                          handleStateChange(
                            selectedOption,
                            values,
                            setFieldValue,
                          );
                        }}
                        value={agencyState}
                      />
                    </SelectWrapper>
                  )}
                </ThreeItemRow>
                <TwoItemRow>
                  <InputText
                    id="contact_name"
                    placeholder="First and Last Name"
                    type="text"
                    label={
                      isCentralStation
                        ? 'Point of Contact *'
                        : 'Agency point of contact *'
                    }
                    maxLength={30}
                    minLength={2}
                    value={values.contact_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    shouldErrorTakeSpace
                    error={
                      errors.contact_name &&
                      (!!values.contact_name || touched.contact_name)
                        ? errors.contact_name
                        : null
                    }
                  />
                  <InputText
                    id="contact_title"
                    placeholder="Title"
                    type="text"
                    label={
                      isCentralStation
                        ? 'Point of Contact Title *'
                        : 'Agency point of contact title *'
                    }
                    maxLength={30}
                    minLength={2}
                    value={values.contact_title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    shouldErrorTakeSpace
                    error={
                      errors.contact_title &&
                      (!!values.contact_title || touched.contact_title)
                        ? errors.contact_title
                        : null
                    }
                  />
                </TwoItemRow>
                <TwoItemRow>
                  <InputText
                    inputType="tel"
                    country="US"
                    data-lpignore="true"
                    id="contact_phone"
                    placeholder="000-000-0000"
                    type="tel"
                    label={
                      isCentralStation
                        ? 'Point of contact phone no.*'
                        : 'Agency point of contact phone no. *'
                    }
                    value={values.contact_phone}
                    onChange={phoneNumber => {
                      setFieldValue('contact_phone', phoneNumber);
                    }}
                    onBlur={phoneNumber => {
                      setFieldTouched('contact_phone', phoneNumber);
                      handleBlur('contact_phone', phoneNumber);
                    }}
                    setFieldValue={setFieldValue}
                    shouldErrorTakeSpace
                    error={
                      errors.contact_phone &&
                      (!!values.contact_phone || touched.contact_phone)
                        ? errors.contact_phone
                        : null
                    }
                  />
                  <InputText
                    id="contact_email"
                    placeholder="Example@gmail.com"
                    type="email"
                    label={
                      isCentralStation
                        ? 'Point of contact email *'
                        : 'Agency point of contact email *'
                    }
                    maxLength={50}
                    value={values.contact_email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    setFieldValue={setFieldValue}
                    shouldErrorTakeSpace
                    error={
                      errors.contact_email &&
                      (!!values.contact_email || touched.contact_email)
                        ? errors.contact_email
                        : null
                    }
                  />
                </TwoItemRow>
                <TwoItemRow>
                  <Box flow={3}>
                    <InputText
                      inputType="tel"
                      country="US"
                      countries={['US']}
                      data-lpignore="true"
                      id="non_emergency_phone"
                      placeholder="Example:0000000000"
                      label="24x7 Non-Emergency Number *"
                      value={values.non_emergency_phone}
                      onChange={phoneNumber => {
                        setFieldValue('non_emergency_phone', phoneNumber);
                      }}
                      onBlur={phoneNumber => {
                        setFieldTouched('non_emergency_phone', phoneNumber);
                        handleBlur('non_emergency_phone', phoneNumber);
                      }}
                      setFieldValue={setFieldValue}
                      shouldErrorTakeSpace
                      error={
                        errors.non_emergency_phone &&
                        (!!values.non_emergency_phone ||
                          touched.non_emergency_phone)
                          ? errors.non_emergency_phone
                          : null
                      }
                    />
                    {isCentralStation && (
                      <Text size="xsmall" color="secondaryText">
                        This number is generally given to alarm companies
                      </Text>
                    )}
                  </Box>
                  {isCentralStation ? null : (
                    <InputText
                      id="population"
                      placeholder="Estimated Population Served"
                      type="text"
                      label={'Estimated Population Served *'}
                      value={values.population}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      setFieldValue={setFieldValue}
                      shouldErrorTakeSpace
                      error={
                        errors.population &&
                        (!!values.population || touched.population)
                          ? errors.population
                          : null
                      }
                    />
                  )}
                </TwoItemRow>
              </Box>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
};

export default AgencyInfoForm;
